import { createStyles, LoadingOverlay, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { useState } from 'react';

import {
  useSubscription,
  useSubscriptionInvoiceItems,
} from '@portals/api/partners';
import { DetailsPanel } from '@portals/core';
import {
  PaymentsHistoryPanel,
  ProductImage,
  SubscriptionStatusBadge,
} from '@portals/framework';
import { ProductPricingModel } from '@portals/types';

import { OverviewTab } from './OverviewTab';
import { UsageBasedSubscriptionDetailsPanelContent } from './UsageBasedSubscriptionDetailsPanelContent';

interface SubscriptionDetailsPanelProps {
  subscriptionId: string;
  onClose: () => void;
}

export function SubscriptionDetailsPanel({
  subscriptionId,
  onClose,
}: SubscriptionDetailsPanelProps) {
  const { classes } = useStyles();

  const [isPaymentHistoryPanelOpen, setIsPaymentHistoryPanelOpen] =
    useState(false);

  const subscription = useSubscription(subscriptionId);
  const subscriptionInvoiceItems = useSubscriptionInvoiceItems(subscriptionId, {
    enabled: isPaymentHistoryPanelOpen,
  });

  const isUsageBased =
    subscription.data?.product?.pricing_model ===
    ProductPricingModel.UsageBased;

  if (subscription.isLoading || !subscription.data) {
    return <LoadingOverlay visible />;
  }

  return (
    <>
      <PaymentsHistoryPanel
        isOpen={isPaymentHistoryPanelOpen}
        invoiceItems={subscriptionInvoiceItems.data || []}
        isFetching={subscriptionInvoiceItems.isFetching}
        currencyCode={subscription.data.currency}
        usageBasedDisplayName={
          subscription.data.product.usage_based_display_name || ''
        }
        onClose={() => setIsPaymentHistoryPanelOpen(false)}
      />
      <DetailsPanel className={classes.detailsPanel}>
        <LoadingOverlay visible={subscription.isLoading} />

        <DetailsPanel.Header onClose={onClose} spacing={0}>
          <Stack align="center">
            <ProductImage
              src={subscription.data?.product?.image_url}
              width={95}
            />

            <DetailsPanel.Title>
              {subscription.data?.product?.name}
            </DetailsPanel.Title>

            <SubscriptionStatusBadge status={subscription.data?.status} />

            {subscription.data?.status === 'pending_cancellation' && (
              <TimeLeftUntilCanceled
                canceledAt={subscription.data?.canceled_at as string}
              />
            )}
          </Stack>
        </DetailsPanel.Header>

        {isUsageBased ? (
          <UsageBasedSubscriptionDetailsPanelContent
            subscription={subscription.data}
            isPaymentHistoryPanelOpen={isPaymentHistoryPanelOpen}
            togglePaymentHistoryPanel={() =>
              setIsPaymentHistoryPanelOpen((prev) => !prev)
            }
          />
        ) : (
          <DetailsPanel.Body>
            <OverviewTab subscription={subscription.data} />
          </DetailsPanel.Body>
        )}
      </DetailsPanel>
    </>
  );
}

const useStyles = createStyles((theme) => ({
  detailsPanel: {
    position: 'relative',
    zIndex: 1,
    backgroundColor: theme.white,
  },
  header: {
    // For the actions menu to render above body content.
    zIndex: 1,
  },
}));

dayjs.extend(relativeTime);

interface TimeLeftUntilCanceledProps {
  canceledAt: string;
}

function TimeLeftUntilCanceled({ canceledAt }: TimeLeftUntilCanceledProps) {
  const timeLeft = dayjs().to(canceledAt);

  return (
    <Text color="amber.8">
      Expires {timeLeft} ({dayjs(canceledAt).format('MMM DD, YYYY')})
    </Text>
  );
}
